import { Controller } from "@hotwired/stimulus"

// The Universal Desk plan picker: a monthly/yearly toggle, click-to-select plan
// cards, and one Continue button that carries the choice through to checkout.
// The proceed button and the hidden fields are absent for anyone who is not the
// account holder, who sees the same prices but cannot buy, so every write to
// them is guarded.
export default class extends Controller {
  static targets = ["card", "radio", "intervalButton", "proceed", "planInput", "intervalInput", "changePlan"]
  static values = { selected: String }

  connect() {
    this.interval = "monthly"
    // Pre-selected when the dashboard sent a plan through; empty otherwise.
    this.selectedPlanId = this.selectedValue || null
    // Arriving with a plan already chosen collapses the list to just that plan,
    // behind a "Change plan" way back; with nothing chosen the full list shows.
    this.expanded = !this.selectedPlanId
    this.render()
  }

  selectCard(event) {
    this.selectedPlanId = event.currentTarget.dataset.planId
    // Choosing collapses the list back down to the pick.
    this.expanded = false
    this.render()
  }

  changePlan() {
    this.expanded = true
    this.render()
  }

  setInterval(event) {
    this.interval = event.currentTarget.dataset.interval
    this.render()
  }

  // type=submit, and the button is disabled until a plan is picked. Writes the
  // choice into the form's hidden fields and lets the (data-turbo=false) form
  // submit through to Stripe Checkout.
  proceed(event) {
    if (!this.selectedPlanId) {
      event.preventDefault()
      return
    }
    this.planInputTarget.value = this.selectedPlanId
    this.intervalInputTarget.value = this.interval
  }

  // Fires on submit, after proceed() has written the values and let it through.
  // The request is already in flight, so disabling the button here cannot
  // cancel it.
  loading() {
    if (!this.hasProceedTarget) return
    this.proceedTarget.disabled = true
    this.proceedTarget.style.opacity = "1"
    this.proceedTarget.textContent = "Taking you to payment…"
  }

  render() {
    // Collapsed = a plan is chosen and the customer has not asked to change it,
    // so only the pick shows, with a "Change plan" way back.
    const collapsed = Boolean(this.selectedPlanId) && !this.expanded
    this.cardTargets.forEach((card, index) => {
      const selected = card.dataset.planId === this.selectedPlanId
      card.classList.toggle("hidden", collapsed && !selected)
      card.classList.toggle("border-primary-500", selected)
      card.classList.toggle("border-2", selected)
      card.classList.toggle("opacity-60", !collapsed && Boolean(this.selectedPlanId) && !selected)
      card.setAttribute("aria-checked", String(selected))

      // The dot is filled by a thick ring rather than a child element, so there
      // is nothing to add or remove as the choice moves between cards.
      const radio = this.radioTargets[index]
      if (!radio) return
      radio.classList.toggle("border-[6px]", selected)
      radio.classList.toggle("border-primary-500", selected)
      radio.classList.toggle("border-2", !selected)
      radio.classList.toggle("border-[var(--pu-border-strong)]", !selected)
    })

    this.intervalButtonTargets.forEach((button) => {
      const active = button.dataset.interval === this.interval
      button.classList.toggle("bg-primary-600", active)
      button.classList.toggle("text-white", active)
      button.classList.toggle("text-[var(--pu-text-muted)]", !active)
    })

    this.element.querySelectorAll("[data-interval-price]").forEach((el) => {
      el.classList.toggle("hidden", el.dataset.intervalPrice !== this.interval)
    })

    if (this.hasChangePlanTarget) this.changePlanTarget.classList.toggle("hidden", !collapsed)

    if (this.hasProceedTarget) this.proceedTarget.disabled = !this.selectedPlanId
  }
}
